import axios from '@/axios'
import { getCookie } from '@/cookies-helper'

export default {
  /**
   * Get list of storage configs available for browsing (S3/S3-compatible only)
   */
  getConfigs () {
    return new Promise((resolve, reject) => {
      axios
        .get('/api/storage-browse/configs')
        .then(response => resolve(response.data))
        .catch(error => reject(error.response))
    })
  },

  /**
   * Browse storage - list files and folders in a given path
   * @param {number} storageConfigId - Storage configuration ID
   * @param {string} path - Optional path to browse (relative to base_folder)
   * @param {string} prefix - Optional additional prefix
   * @param {string} continuationToken - Optional token for pagination (load more)
   */
  browse (storageConfigId, path = '', prefix = '', continuationToken = null) {
    return new Promise((resolve, reject) => {
      axios
        .post('/api/storage-browse/browse', { storageConfigId, path, prefix, continuationToken })
        .then(response => resolve(response.data))
        .catch(error => reject(error.response))
    })
  },

  /**
   * Search files in storage
   * @param {number} storageConfigId - Storage configuration ID
   * @param {string} searchTerm - Term to search for
   * @param {string} path - Optional path to search within
   * @param {number} maxResults - Max results to return
   */
  search (storageConfigId, searchTerm, path = '', maxResults = 100) {
    return new Promise((resolve, reject) => {
      axios
        .post('/api/storage-browse/search', { storageConfigId, searchTerm, path, maxResults })
        .then(response => resolve(response.data))
        .catch(error => reject(error.response))
    })
  },

  /**
   * Get detailed info about a file
   * @param {number} storageConfigId - Storage configuration ID
   * @param {string} filePath - Full path to file
   */
  getFileInfo (storageConfigId, filePath) {
    return new Promise((resolve, reject) => {
      axios
        .post('/api/storage-browse/file-info', { storageConfigId, filePath })
        .then(response => resolve(response.data))
        .catch(error => reject(error.response))
    })
  },

  /**
   * Get suggested segment files based on episode metadata
   * @param {number} episodeId - Episode ID
   * @param {number} storageConfigId - Storage configuration ID
   * @param {string} path - Optional path to search within
   * @param {string} customPattern - Optional custom pattern (learned from example, season-specific)
   */
  suggestSegmentFiles (episodeId, storageConfigId, path = '', customPattern = null) {
    return new Promise((resolve, reject) => {
      axios
        .post('/api/storage-browse/suggest-segments', { episodeId, storageConfigId, path, customPattern })
        .then(response => resolve(response.data))
        .catch(error => reject(error.response))
    })
  },

  /**
   * Get presigned URL for a file (for preview/download)
   * @param {number} storageConfigId - Storage configuration ID
   * @param {string} filePath - Full path to file
   * @param {number} expiresIn - URL expiration in seconds (default 3600 = 1 hour)
   */
  getPresignedUrl (storageConfigId, filePath, expiresIn = 3600) {
    return new Promise((resolve, reject) => {
      axios
        .post('/api/storage-browse/presigned-url', { storageConfigId, filePath, expiresIn })
        .then(response => resolve(response.data))
        .catch(error => reject(error.response))
    })
  },

  /**
   * Get proxy stream URL for a file (bypasses CORS by streaming through backend)
   * Use this when presigned URLs fail due to CORS restrictions
   * @param {number} storageConfigId - Storage configuration ID
   * @param {string} filePath - Full path to file
   * @returns {string} URL to the proxy stream endpoint with auth token
   */
  getProxyStreamUrl (storageConfigId, filePath) {
    // Build URL with query parameters including auth token
    const baseUrl = axios.defaults.baseURL || ''
    // Token is stored in cookie, not localStorage
    const token = getCookie('token')
    const params = new URLSearchParams({
      storageConfigId: storageConfigId,
      filePath: filePath
    })
    // Include token for authentication (required since this is a direct URL, not an axios request)
    if (token) {
      params.append('token', token)
    }
    return `${baseUrl}/api/storage-browse/proxy-stream?${params.toString()}`
  },

  /**
   * Learn a filename pattern from an example file
   * @param {string} exampleFilename - Example filename to learn pattern from
   * @param {string} showTitle - Show/media title
   * @param {number} seasonNumber - Season number (optional)
   * @param {number} episodeNumber - Episode number (optional)
   * @param {number} segmentNumber - Segment number (optional)
   * @returns {Object} { success, pattern, confidence, matchedParts }
   */
  learnPatternFromExample (exampleFilename, showTitle, seasonNumber = null, episodeNumber = null, segmentNumber = null) {
    return new Promise((resolve, reject) => {
      axios
        .post('/api/storage-browse/learn-pattern', {
          exampleFilename,
          showTitle,
          seasonNumber,
          episodeNumber,
          segmentNumber
        })
        .then(response => resolve(response.data))
        .catch(error => reject(error.response))
    })
  }
}
